import { createStowagePlanChange } from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowageChangeDataType } from 'src/types/ViewModel/Stowage/Enums/StowageChangeDataType';
import { RotationBerthCall } from 'src/types/ViewModel/Stowage/Rotation/RotationBerthCall';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { isValidCargoDropTarget } from './dragAndDrop/dragAndDropCargoOperationUtils';
import { UUID } from 'crypto';

export const moveCargoOperation = (
    stowagePlan: StowagePlan,
    sourceCargoOperation: RotationCargoOperation,
    targetBerthCallData: RotationBerthCall,
    targetCargoOperation?: RotationCargoOperation | null
): StowagePlanChange[] => {
    if (!sourceCargoOperation || targetCargoOperation?.cargoOperationGuid === sourceCargoOperation.cargoOperationGuid) {
        return [];
    }

    if (isValidCargoDropTarget(targetBerthCallData, stowagePlan.rotation) === false) {
        return [];
    }

    const sourceBerthCall = stowagePlan.rotation.flatMap((pc) => pc.berthCalls).find((bc) => bc.berthCallGuid === sourceCargoOperation.berthCallGuid);

    const targetBerthCall = stowagePlan.rotation.flatMap((pc) => pc.berthCalls).find((bc) => bc.berthCallGuid === targetBerthCallData.berthCallGuid);

    if (!sourceBerthCall || !targetBerthCall) {
        return [];
    }

    const isSameBerthCall = sourceBerthCall.berthCallGuid === targetBerthCall.berthCallGuid;

    if (isSameBerthCall) {
        // If dropping onto the same berth call without a specific target operation, reject it
        // This prevents accidental drops where the user didn't intend to move anything
        if (!targetCargoOperation) {
            return [];
        }
        return moveWithinSameBerthCall(stowagePlan, sourceCargoOperation, sourceBerthCall, targetCargoOperation);
    } else {
        return moveToDifferentBerthCall(stowagePlan, sourceCargoOperation, sourceBerthCall, targetBerthCall, targetCargoOperation);
    }
};

const moveWithinSameBerthCall = (
    stowagePlan: StowagePlan,
    sourceOperation: RotationCargoOperation,
    berthCall: RotationBerthCall,
    targetOperation: RotationCargoOperation | null | undefined
): StowagePlanChange[] => {
    const operations = berthCall.cargoOperations;
    const sourceIndex = operations.findIndex((op) => op.cargoOperationGuid === sourceOperation.cargoOperationGuid);

    if (sourceIndex === -1) {
        return [];
    }

    let targetIndex: number;
    if (targetOperation) {
        targetIndex = operations.findIndex((op) => op.cargoOperationGuid === targetOperation.cargoOperationGuid);
        if (targetIndex === -1) {
            return [];
        }
    } else {
        targetIndex = operations.length - 1;
    }

    if (sourceIndex === targetIndex) {
        return [];
    }

    const changes: StowagePlanChange[] = [];

    // Store original indices BEFORE splicing for ALL operations
    const originalIndices = new Map<string, number>();
    operations.forEach((op, idx) => {
        originalIndices.set(op.cargoOperationGuid, idx);
    });

    // Remove source from its current position
    const [movedOp] = operations.splice(sourceIndex, 1);

    // Insert at target position
    operations.splice(targetIndex, 0, movedOp);

    // Now reassign sequence numbers based on the new order
    // Track which operations need changes
    for (let i = 0; i < operations.length; i++) {
        const operation = operations[i];
        const newSequenceNumber = i + 1; // 1-based sequence numbers

        if (operation.cargoOperationSequenceNumber !== newSequenceNumber) {
            // Use the original index if we stored it, otherwise use the current index
            const originalIndex = originalIndices.get(operation.cargoOperationGuid) ?? i;
            trackSequenceChange(operation, originalIndex);

            // Determine the actual sequence number to use
            let actualSequenceNumber = newSequenceNumber;
            const isBackToOriginal = isBackToOriginalPlace(operation, operation.berthCallGuid, i);

            // Check if this operation is back to its original place
            if (isBackToOriginal) {
                actualSequenceNumber = operation.hasChangedSequence!.originalSequenceNumber;
                // Clear the tracking metadata
                operation.hasChangedSequence = null;
            }

            const changeRecord = createChangeForOperation(stowagePlan.stowagePlanGuid, operation, actualSequenceNumber, i);
            changes.push(changeRecord);

            operation.cargoOperationSequenceNumber = actualSequenceNumber;
        }
    }

    return changes;
};

const moveToDifferentBerthCall = (
    stowagePlan: StowagePlan,
    sourceOperation: RotationCargoOperation,
    sourceBerthCall: RotationBerthCall,
    targetBerthCall: RotationBerthCall,
    targetCargoOperation?: RotationCargoOperation | null
): StowagePlanChange[] => {
    const sourceIndex = sourceBerthCall.cargoOperations.findIndex((op) => op.cargoOperationGuid === sourceOperation.cargoOperationGuid);

    if (sourceIndex === -1) {
        return [];
    }

    trackSequenceChange(sourceOperation, sourceIndex);

    const changeRecords: StowagePlanChange[] = [];

    // First, process remaining operations in SOURCE berth (after removing the moving operation)
    // Store original indices before any modifications
    const originalIndicesInSource = new Map<UUID, number>();
    sourceBerthCall.cargoOperations.forEach((op, idx) => {
        originalIndicesInSource.set(op.cargoOperationGuid, idx);
    });

    // Process operations that will remain in source berth (excluding the one being moved)
    sourceBerthCall.cargoOperations.forEach((operation, index) => {
        if (operation.cargoOperationGuid === sourceOperation.cargoOperationGuid) {
            return; // Skip the operation being moved
        }

        const originalIndex = originalIndicesInSource.get(operation.cargoOperationGuid)!;
        const expectedSequence = index < sourceIndex ? index + 1 : index; // Adjust for removed operation

        if (operation.cargoOperationSequenceNumber !== expectedSequence || !operation.hasChangedSequence) {
            // Track if not already tracked
            if (!operation.hasChangedSequence) {
                trackSequenceChange(operation, originalIndex);
            }

            // Check if back to original (using the index it will have AFTER the moving operation is removed)
            const indexAfterRemoval = index > sourceIndex ? index - 1 : index;
            const backToOriginal = isBackToOriginalPlace(operation, sourceBerthCall.berthCallGuid, indexAfterRemoval);

            let actualSequence = expectedSequence;
            const operationChanges: any = {
                berthCallGuid: sourceBerthCall.berthCallGuid,
                portCallGuid: sourceBerthCall.portCallGuid,
                cargoOperationSequenceNumber: expectedSequence,
            };

            if (backToOriginal) {
                actualSequence = operation.hasChangedSequence!.originalSequenceNumber;
                operationChanges.cargoOperationSequenceNumber = actualSequence;
                operationChanges.hasChangedSequence = null;
            } else if (operation.hasChangedSequence) {
                operationChanges.hasChangedSequence = operation.hasChangedSequence;
            }

            operation.cargoOperationSequenceNumber = actualSequence;

            changeRecords.push(
                createStowagePlanChange(
                    stowagePlan.stowagePlanGuid,
                    operation.cargoOperationGuid,
                    StowageChangeDataType.RotationCargoOperation,
                    operationChanges
                )
            );
        }
    });

    // Determine where to insert the operation in the target berth
    let targetIndex: number;
    if (targetCargoOperation) {
        targetIndex = targetBerthCall.cargoOperations.findIndex((op) => op.cargoOperationGuid === targetCargoOperation.cargoOperationGuid);
        if (targetIndex === -1) {
            // Target operation not found, add to end
            targetIndex = targetBerthCall.cargoOperations.length;
        }
    } else {
        // No target operation specified, add to end
        targetIndex = targetBerthCall.cargoOperations.length;
    }

    // Track original indices for operations already in target berth BEFORE inserting
    const originalIndicesInTarget = new Map<UUID, number>();
    targetBerthCall.cargoOperations.forEach((op, idx) => {
        originalIndicesInTarget.set(op.cargoOperationGuid, idx);
    });

    // Insert the moving operation at the target index
    targetBerthCall.cargoOperations.splice(targetIndex, 0, sourceOperation);

    // Now process ALL operations in target berth (including the newly inserted one)
    targetBerthCall.cargoOperations.forEach((operation, index) => {
        const expectedSequence = index + 1; // 1-based consecutive sequence

        // For the moving operation, we already tracked it earlier
        // For existing operations, track if not already tracked
        if (operation.cargoOperationGuid !== sourceOperation.cargoOperationGuid) {
            const originalIndex = originalIndicesInTarget.get(operation.cargoOperationGuid)!;
            if (!operation.hasChangedSequence) {
                trackSequenceChange(operation, originalIndex);
            }
        }

        // Always check if back to original, even if sequence matches expected
        const backToOriginal = operation.hasChangedSequence && isBackToOriginalPlace(operation, targetBerthCall.berthCallGuid, index);

        // Determine if we need to create a change record
        const needsChange = operation.cargoOperationSequenceNumber !== expectedSequence || backToOriginal;

        if (needsChange) {
            let actualSequence = expectedSequence;
            const operationChanges: any = {
                berthCallGuid: targetBerthCall.berthCallGuid,
                portCallGuid: targetBerthCall.portCallGuid,
                cargoOperationSequenceNumber: expectedSequence,
            };

            if (backToOriginal) {
                actualSequence = operation.hasChangedSequence!.originalSequenceNumber;
                operationChanges.cargoOperationSequenceNumber = actualSequence;
                operationChanges.hasChangedSequence = null;
                operation.hasChangedSequence = null;
            } else if (operation.hasChangedSequence) {
                operationChanges.hasChangedSequence = operation.hasChangedSequence;
            }

            operation.cargoOperationSequenceNumber = actualSequence;

            changeRecords.push(
                createStowagePlanChange(
                    stowagePlan.stowagePlanGuid,
                    operation.cargoOperationGuid,
                    StowageChangeDataType.RotationCargoOperation,
                    operationChanges
                )
            );
        }
    });

    return changeRecords;
};

const createChangeForOperation = (
    stowagePlanGuid: UUID,
    operation: RotationCargoOperation,
    sequenceNumber: number,
    newIndex: number
): StowagePlanChange => {
    const changes: any = {
        cargoOperationSequenceNumber: sequenceNumber,
    };

    // If back to original place, clear the marker
    if (isBackToOriginalPlace(operation, operation.berthCallGuid, newIndex)) {
        changes.hasChangedSequence = null;
    } else if (operation.hasChangedSequence) {
        // Preserve the tracking metadata
        changes.hasChangedSequence = operation.hasChangedSequence;
    }

    return createStowagePlanChange(stowagePlanGuid, operation.cargoOperationGuid, StowageChangeDataType.RotationCargoOperation, changes);
};

// Helper function to track sequence changes
const trackSequenceChange = (operation: RotationCargoOperation, index: number): any => {
    const changes: any = {};
    if (!operation.hasChangedSequence) {
        operation.hasChangedSequence = {
            originalSequenceNumber: operation.cargoOperationSequenceNumber,
            originalIndexInBerthCall: index,
            originalParentGuid: operation.berthCallGuid,
        };
        changes.hasChangedSequence = operation.hasChangedSequence;
    }
    return changes;
};

// Helper function to check if an operation is back to its original place
const isBackToOriginalPlace = (operation: RotationCargoOperation, parentGuid: string, index: number): boolean => {
    if (!operation.hasChangedSequence) return false;

    const isBackToOriginalParent = operation.hasChangedSequence.originalParentGuid === parentGuid;
    const isBackToOriginalIndex = operation.hasChangedSequence.originalIndexInBerthCall === index;
    return isBackToOriginalParent && isBackToOriginalIndex;
};
